import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  strategyApi,
  type StrategyGoal,
  type StrategyDirection,
  type StrategyMeasure,
  type StrategicDashboard,
  type ManagementDashboard,
  type OperationalDashboard,
  type FunctionalDashboard,
  type StrategyReport,
  type MeasureStatus,
} from '@/plugins/api'

export const useStrategyStore = defineStore('strategy', () => {
  // State
  const goals = ref<StrategyGoal[]>([])
  const directions = ref<StrategyDirection[]>([])
  const measures = ref<StrategyMeasure[]>([])
  const currentGoal = ref<StrategyGoal | null>(null)
  const currentDirection = ref<StrategyDirection | null>(null)
  const currentMeasure = ref<StrategyMeasure | null>(null)
  const strategicDashboard = ref<StrategicDashboard | null>(null)
  const managementDashboard = ref<ManagementDashboard | null>(null)
  const operationalDashboard = ref<OperationalDashboard | null>(null)
  const functionalDashboard = ref<FunctionalDashboard | null>(null)
  const aiReports = ref<StrategyReport[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Classification
  const classifications = ref<any[]>([])
  const classificationStats = ref<Record<string, number>>({})
  const classificationsLoading = ref(false)
  const classificationsTotal = ref(0)

  // Getters
  const overallProgress = computed(() => {
    if (!goals.value.length) return 0
    return Math.round(
      goals.value.reduce((s, g) => s + Number(g.progress), 0) / goals.value.length * 100
    ) / 100
  })

  const criticalGaps = computed(() =>
    directions.value.filter(d => d.targetScore - d.currentScore > 20)
  )

  const overdueMeasures = computed(() =>
    measures.value.filter(
      m => m.deadline && new Date(m.deadline) < new Date() && Number(m.progress) < 80
    )
  )

  const statusLabels: Record<MeasureStatus, string> = {
    planned: 'Запланировано',
    in_progress: 'В работе',
    completed: 'Завершено',
    delayed: 'Отложено',
    cancelled: 'Отменено',
  }

  const statusColors: Record<MeasureStatus, string> = {
    planned: 'grey',
    in_progress: 'blue',
    completed: 'success',
    delayed: 'warning',
    cancelled: 'error',
  }

  // Actions
  async function fetchGoals() {
    loading.value = true
    error.value = null
    try {
      const { data } = await strategyApi.getGoals()
      goals.value = data
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки целей'
      console.error('Failed to load goals:', e)
    } finally {
      loading.value = false
    }
  }

  async function fetchDirections() {
    loading.value = true
    error.value = null
    try {
      const { data } = await strategyApi.getDirections()
      directions.value = data
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки направлений'
      console.error('Failed to load directions:', e)
    } finally {
      loading.value = false
    }
  }

  async function fetchDirection(id: string) {
    loading.value = true
    error.value = null
    try {
      const { data } = await strategyApi.getDirection(id)
      currentDirection.value = data
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки направления'
      console.error('Failed to load direction:', e)
    } finally {
      loading.value = false
    }
  }

  async function fetchMeasures(directionId?: string) {
    loading.value = true
    error.value = null
    try {
      const { data } = await strategyApi.getMeasures(directionId ? { directionId } : undefined)
      measures.value = data
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки мероприятий'
      console.error('Failed to load measures:', e)
    } finally {
      loading.value = false
    }
  }

  async function fetchMeasure(id: string) {
    loading.value = true
    error.value = null
    try {
      const { data } = await strategyApi.getMeasure(id)
      currentMeasure.value = data
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки мероприятия'
      console.error('Failed to load measure:', e)
    } finally {
      loading.value = false
    }
  }

  type DashboardLevel = 'strategic' | 'management' | 'operational' | 'functional'

  async function fetchDashboard(level: DashboardLevel) {
    loading.value = true
    error.value = null
    try {
      const apiMap: Record<DashboardLevel, () => Promise<{ data: unknown }>> = {
        strategic: strategyApi.getStrategicDashboard,
        management: strategyApi.getManagementDashboard,
        operational: strategyApi.getOperationalDashboard,
        functional: strategyApi.getFunctionalDashboard,
      }
      const { data } = await apiMap[level]()
      switch (level) {
        case 'strategic': strategicDashboard.value = data as StrategicDashboard; break
        case 'management': managementDashboard.value = data as ManagementDashboard; break
        case 'operational': operationalDashboard.value = data as OperationalDashboard; break
        case 'functional': functionalDashboard.value = data as FunctionalDashboard; break
      }
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки дашборда'
      console.error('Failed to load dashboard:', e)
    } finally {
      loading.value = false
    }
  }

  async function linkTasks(measureId: string, taskIds: string[], weight?: number) {
    try {
      await strategyApi.linkTasks(measureId, taskIds, weight)
      if (currentMeasure.value?.id === measureId) {
        await fetchMeasure(measureId)
      }
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка привязки задач'
      throw e
    }
  }

  async function updateManualProgress(measureId: string, progress: number) {
    try {
      await strategyApi.updateProgress(measureId, progress)
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка обновления прогресса'
      throw e
    }
  }

  async function recalculate() {
    loading.value = true
    try {
      await strategyApi.recalculate()
      // Sequential to avoid shared loading ref race condition
      await fetchGoals()
      await fetchDirections()
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Ошибка пересчёта'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function fetchAIReports(params?: { type?: string; limit?: number }) {
    try {
      const { data } = await strategyApi.getAIReports(params)
      aiReports.value = data
    } catch (e: unknown) {
      console.error('Failed to load AI reports:', e)
    }
  }

  // Classification actions
  async function fetchClassifications(params?: Record<string, any>, append = false) {
    classificationsLoading.value = true
    try {
      const { data } = await strategyApi.getClassifications(params)
      if (append) {
        classifications.value = [...classifications.value, ...data.items]
      } else {
        classifications.value = data.items
      }
      classificationsTotal.value = data.total
    } catch (e: unknown) {
      console.error('Failed to load classifications:', e)
    } finally {
      classificationsLoading.value = false
    }
  }

  async function fetchClassificationStats() {
    try {
      const { data } = await strategyApi.getClassificationStats()
      classificationStats.value = data
    } catch (e: unknown) {
      console.error('Failed to load classification stats:', e)
    }
  }

  async function reviewClassification(
    id: string,
    status: string,
    weight?: number,
  ) {
    await strategyApi.reviewClassification(id, { status, weight })
    await fetchClassifications()
    await fetchClassificationStats()
  }

  async function batchReviewClassifications(ids: string[], status: string) {
    await strategyApi.batchReviewClassifications({ ids, status })
    await fetchClassifications()
    await fetchClassificationStats()
  }

  async function classifyTask(taskId: string) {
    await strategyApi.classifyTask(taskId)
  }

  // Realtime: apply progress delta from STOMP
  function applyProgressDelta(delta: {
    directionId?: string
    directionProgress?: number
    goals?: Array<{ goalId: string; progress: number }>
    overallProgress?: number
  }) {
    if (delta.directionId) {
      const dir = directions.value.find(d => d.id === delta.directionId)
      if (dir && delta.directionProgress != null) {
        dir.progress = delta.directionProgress
      }
    }
    for (const g of delta.goals || []) {
      const goal = goals.value.find(x => x.id === g.goalId)
      if (goal) goal.progress = g.progress
    }
  }

  // Helpers
  function getStatusLabel(status: MeasureStatus) {
    return statusLabels[status] ?? status
  }

  function getStatusColor(status: MeasureStatus) {
    return statusColors[status] ?? 'grey'
  }

  function getProgressColor(progress: number) {
    if (progress <= 20) return 'red'
    if (progress <= 40) return 'orange'
    if (progress <= 60) return 'amber'
    if (progress <= 80) return 'light-green'
    return 'green'
  }

  return {
    // State
    goals, directions, measures,
    currentGoal, currentDirection, currentMeasure,
    strategicDashboard, managementDashboard, operationalDashboard, functionalDashboard,
    aiReports, loading, error,

    // Getters
    overallProgress, criticalGaps, overdueMeasures,

    // Actions
    fetchGoals, fetchDirections, fetchDirection,
    fetchMeasures, fetchMeasure, fetchDashboard,
    linkTasks, updateManualProgress, recalculate,
    fetchAIReports, applyProgressDelta,

    // Classification
    classifications, classificationStats, classificationsLoading, classificationsTotal,
    fetchClassifications, fetchClassificationStats,
    reviewClassification, batchReviewClassifications, classifyTask,

    // Helpers
    getStatusLabel, getStatusColor, getProgressColor,
  }
})
